import { BalanceData } from "../data/balance";
import { PetData } from "../data/pets";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";

/**
 * Which pet is wild on a run (Pets v3, docs/PETS.md "Which pet is wild"; the app asks, it knows what is owned, and puts
 * the pet in the run's WildPetRule — the sim is the same for every pet):
 * - each pet roams from its `roamFromLevel` (its debut);
 * - a pet's roam level, played while that pet is not owned, always brings it (a new player meets them one at a time);
 * - otherwise a draw from (seed, level) — tag "wildPick", never the run's RNG — among the pets roaming at that level, an
 *   uncaught pet weighing `wild.uncaughtWeight` and a caught one `wild.caughtWeight`, so the collection fills first and
 *   then every pet keeps turning up to level. A checkpoint retry replays the same seed and level, so the same pet.
 */

/** The pets roaming on campaign level `level` (their roam level reached), in roster order. */
export function roamingPets(balance: BalanceData, level: number): PetData[] { return balance.pets.list.filter((p) => p.roamFromLevel <= level); }

/** The pet whose debut `level` is, not owned yet, or null. */
export function debutPet(balance: BalanceData, level: number, owns: (petId: string) => boolean): PetData | null {
  return balance.pets.list.find((p) => p.roamFromLevel === level && !owns(p.id)) ?? null;
}

const PICK_TAG = "wildPick";

/** The wild pet of a run of `level` with `seed` (see the module note), or null when none roams there yet. */
export function wildPetFor(balance: BalanceData, level: number, seed: bigint, owns: (petId: string) => boolean): PetData | null {
  const debut = debutPet(balance, level, owns);
  if (debut !== null) return debut;
  const roaming = roamingPets(balance, level);
  if (roaming.length <= 1) return roaming[0] ?? null;
  const W = balance.pets.wild, weight = (p: PetData): number => (owns(p.id) ? W.caughtWeight : W.uncaughtWeight);
  const total = roaming.reduce((a, p) => a + weight(p), 0);
  const h = new Fnv1a64();
  h.writeString(PICK_TAG); h.writeLong(seed); h.writeInt(level);
  let pick = new Pcg32(h.value).nextInt(total);
  for (const p of roaming) { pick -= weight(p); if (pick < 0) return p; }
  return roaming[roaming.length - 1];
}
